import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';

const PrivacyPolicy = () => {
  return (
    <MarketingLayout lightTheme>
      <SEO {...SEO_CONFIGS.privacy} />
      <section className="pt-32 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">Privacy Policy</h1>
            <p className="text-slate-500 mb-8">Last updated: January 2026</p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              <p className="text-slate-600 mb-8 leading-relaxed">
                Nanokart is committed to protecting your privacy and complies with applicable Indian data protection laws, 
                including the Digital Personal Data Protection Act, 2023.
              </p>

              <div className="space-y-8">
                {/* Section 1 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
                  
                  <div className="space-y-4 ml-4">
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">a. Account Information</h3>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>Business name</li>
                        <li>Contact details</li>
                        <li>Billing information</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">b. API Usage Data</h3>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>Uploaded images</li>
                        <li>Generated outputs</li>
                        <li>Logs, metadata, customer & SKU identifiers</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">c. Technical Data</h3>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>IP address</li>
                        <li>Device & browser data</li>
                        <li>Cookies</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">2. How We Use Information</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Provide and maintain services</li>
                    <li>Process API requests</li>
                    <li>Billing & account management</li>
                    <li>Security and fraud prevention</li>
                    <li>Service improvements (with consent)</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">3. Image Data Processing</h2>
                  <p className="text-slate-600 mb-4">Uploaded images:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Are processed only to deliver requested outputs</li>
                    <li>Are cached temporarily for performance</li>
                    <li>Are encrypted at rest and in transit</li>
                    <li>Are never used for training without consent</li>
                  </ul>
                  <p className="text-slate-600 mt-4 font-medium">
                    You may request deletion at any time.
                  </p>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">4. Data Sharing</h2>
                  <p className="text-slate-600 mb-4">We may share data with:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Cloud providers (e.g., AWS)</li>
                    <li>CDN providers</li>
                    <li>Payment processors</li>
                    <li>Legal authorities (when required)</li>
                  </ul>
                  <p className="text-slate-600 mt-4 font-medium text-blue-700">
                    We do not sell personal data.
                  </p>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">5. Data Retention</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Account data retained while active</li>
                    <li>Logs retained per subscription plan (30-90 days)</li>
                    <li>Deleted within 30 days after account closure (unless legally required)</li>
                  </ul>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">6. Security</h2>
                  <p className="text-slate-600 mb-4">We implement:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>TLS 1.3 encryption in transit</li>
                    <li>AES-256 encryption at rest</li>
                    <li>Access controls and monitoring</li>
                  </ul>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">7. Your Rights</h2>
                  <p className="text-slate-600 mb-4">Depending on jurisdiction, you may:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Access, correct, or delete data</li>
                    <li>Export your data</li>
                    <li>Withdraw consent</li>
                    <li>Opt-out of marketing</li>
                  </ul>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">8. International Transfers</h2>
                  <p className="text-slate-600">
                    Data may be processed outside India using contractual and technical safeguards.
                  </p>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">9. Children's Privacy</h2>
                  <p className="text-slate-600">
                    Services are not intended for individuals under 18.
                  </p>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">10. Policy Updates</h2>
                  <p className="text-slate-600">
                    Updates will be posted with revised dates.
                  </p>
                </div>

                {/* Section 11 - Contact */}
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">11. Contact</h2>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <a href="mailto:privacy@nanokart.ai" className="text-blue-600 font-medium hover:underline">
                          privacy@nanokart.ai
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Building className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Company</p>
                        <p className="text-slate-700 font-medium">NanoKart India Private Limited, Bangalore, India</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default PrivacyPolicy;
